/**
 * @module data/insights
 *
 * Thought leadership: articles, whitepapers and research notes. Kept as data so
 * the insights section, the search index and the footer can all read one list.
 */

/**
 * @typedef {Object} Insight
 * @property {string} id
 * @property {"Article"|"Whitepaper"|"Research"|"Field note"} type
 * @property {string} title
 * @property {string} abstract
 * @property {string} topic
 * @property {string} date        ISO date.
 * @property {string} readingTime e.g. `"9 min"`.
 * @property {string[]} tags
 * @property {string} accent
 * @property {boolean} [featured]
 * @property {string} href        Destination (placeholder in this build).
 */

/** @type {Insight[]} */
export const INSIGHTS = [
  {
    id: "method-before-motion",
    type: "Article",
    title: "Method before motion: why AI programmes stall at the second quarter",
    abstract:
      "Most AI initiatives do not fail at the model. They fail when the pilot meets the operating model — no owner, no evaluation baseline, no decision rights. A field analysis of forty-one stalled programmes and the four governance gaps they share.",
    topic: "Governance",
    date: "2026-08-18",
    readingTime: "11 min",
    tags: ["governance", "delivery", "operating model"],
    accent: "#e0b584",
    featured: true,
    href: "#insights",
  },
  {
    id: "data-contracts",
    type: "Whitepaper",
    title: "Data contracts as architecture: the artefact that makes AI defensible",
    abstract:
      "If you cannot say who owns an entity, what its quality threshold is and where it came from, you cannot defend an automated decision that used it. A practical template for domain data contracts, with worked examples for six common entities.",
    topic: "Data & AI Architecture",
    date: "2026-07-02",
    readingTime: "18 min",
    tags: ["data", "contracts", "lineage", "architecture"],
    accent: "#6f9fbe",
    featured: true,
    href: "#insights",
  },
  {
    id: "assemble-first",
    type: "Article",
    title: "Assemble, don't build: a sourcing heuristic for AI capability",
    abstract:
      "Build, buy and configure were enough when software was the product. When the model is a service and your value is the context layer, there is a fourth route. When to assemble, and what you must own to make it safe.",
    topic: "Sourcing",
    date: "2026-06-11",
    readingTime: "8 min",
    tags: ["sourcing", "platform", "strategy"],
    accent: "#a98bc4",
    href: "#insights",
  },
  {
    id: "agent-accountability",
    type: "Research",
    title: "Who is accountable for an agent? Naming owners for autonomous work",
    abstract:
      "Across 130 deployed agentic systems we asked a single question: who can switch it off, and who answers for its output? In a third of cases the answer was 'the team that built it' — a team that no longer existed. A model for durable ownership.",
    topic: "Responsible AI",
    date: "2026-05-27",
    readingTime: "14 min",
    tags: ["agents", "accountability", "responsible ai"],
    accent: "#5fd4c4",
    href: "#insights",
  },
  {
    id: "cost-of-inference",
    type: "Field note",
    title: "Inference is a running cost, and your architecture decides how large",
    abstract:
      "Two enterprises, the same task, a forty-fold difference in unit cost. The gap was not the model — it was routing, caching, retrieval discipline and evaluation thresholds. A short, uncomfortable note on designing for unit economics.",
    topic: "Technology",
    date: "2026-04-15",
    readingTime: "6 min",
    tags: ["cost", "platform", "routing"],
    accent: "#8292ad",
    href: "#insights",
  },
  {
    id: "capability-heat-map",
    type: "Whitepaper",
    title: "Evidence-based capability heat maps: mining the enterprise instead of asking it",
    abstract:
      "Workshop-derived heat maps encode the loudest voice in the room. This paper sets out a method for deriving capability maturity from process logs, effort data and outcome measures — and the political consequences of doing so.",
    topic: "Business Architecture",
    date: "2026-03-09",
    readingTime: "16 min",
    tags: ["business architecture", "process mining", "measurement"],
    accent: "#c9825a",
    href: "#insights",
  },
];

/** Section copy. */
export const INSIGHTS_SUMMARY = {
  eyebrow: "Insights",
  title: "What we have learned, written down",
  lede:
    "We publish the method, not just the marketing. Articles, whitepapers and field notes drawn from live engagements — including the parts that did not go to plan.",
};

/**
 * Distinct topics across all insights, for filter chips.
 *
 * @returns {string[]}
 */
export function getInsightTopics() {
  return Array.from(new Set(INSIGHTS.map((insight) => insight.topic))).sort();
}
